import React from 'react'
import { Link } from 'react-router-dom'
import { User, Calendar, Check, X, MessageSquare } from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { MatchingRequest } from '@/types/matching'
import RatingSummary from './RatingSummary'

interface MenteeRequestCardProps {
  request: MatchingRequest
  onAccept: (requestId: string) => void
  onDecline: (requestId: string) => void
  isProcessing?: boolean
  processingAction?: 'accept' | 'decline' | null
}

const MenteeRequestCard: React.FC<MenteeRequestCardProps> = ({
  request,
  onAccept,
  onDecline,
  isProcessing = false,
  processingAction = null
}) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <Card className="hover:shadow-md transition-shadow border-l-4 border-l-yellow-400">
      <CardBody>
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <Link
            to={request.mentee ? `/users/${request.mentee.id}` : '#'}
            className="flex-shrink-0"
          >
            {request.mentee?.profile?.profilePicture ? (
              <img
                src={request.mentee.profile.profilePicture}
                alt={`${request.mentee.firstName} ${request.mentee.lastName}`}
                className="w-14 h-14 rounded-full object-cover"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-yellow-100 flex items-center justify-center">
                <User className="w-7 h-7 text-yellow-600" />
              </div>
            )}
          </Link>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {request.mentee && (
                    <Link
                      to={`/users/${request.mentee.id}`}
                      className="hover:underline hover:text-primary-700"
                    >
                      {request.mentee.firstName} {request.mentee.lastName}
                    </Link>
                  )}
                </h3>
                <p className="text-sm text-gray-500">{request.mentee?.email}</p>
                <div className="mt-1">
                  <RatingSummary
                    averageRating={request.mentee?.averageRating}
                    totalReviews={request.mentee?.totalReviews}
                  />
                </div>
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(request.createdAt)}</span>
              </div>
            </div>

            {/* Interests & Goals */}
            {request.mentee?.profile && (
              <div className="mt-2 space-y-1">
                {request.mentee.profile.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {request.mentee.profile.interests.slice(0, 4).map((interest) => (
                      <span
                        key={interest}
                        className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                )}
                {request.mentee.profile.goals?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {request.mentee.profile.goals.slice(0, 2).map((goal) => (
                      <span
                        key={goal}
                        className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs"
                      >
                        {goal}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Message */}
            {request.message && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700 italic">"{request.message}"</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => onAccept(request.id)}
                disabled={isProcessing}
                isLoading={isProcessing && processingAction === 'accept'}
              >
                <Check className="w-4 h-4 mr-1" />
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDecline(request.id)}
                disabled={isProcessing}
                isLoading={isProcessing && processingAction === 'decline'}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-1" />
                Decline
              </Button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export default MenteeRequestCard
